import { cn, Input, type InputProps, useDebounce } from "@aurem/ui";
import { SearchIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "../i18n";

type Props = InputProps & {
  /** Debounce delay in ms (default: 300) */
  debounce?: number;
};

export const SearchInput = ({
  debounce = 300,
  value,
  onChange,
  onClear,
  startAccessory,
  className,
  placeholder,
  ...rest
}: Props) => {
  const { t } = useTranslations();

  const [searchValue, setSearchValue] = useState(value?.toString() ?? "");

  const { debouncedValue, setDebouncedValue, cancel } = useDebounce<string>(
    searchValue,
    debounce,
  );

  const prevDebouncedValueRef = useRef(debouncedValue);

  useEffect(() => {
    if (value !== undefined) {
      setSearchValue(value.toString());
    }
  }, [value]);

  useEffect(() => {
    if (onChange && debouncedValue !== prevDebouncedValueRef.current) {
      onChange(debouncedValue);
      prevDebouncedValueRef.current = debouncedValue;
    }
  }, [debouncedValue, onChange]);

  const handleClear = onClear
    ? () => {
        cancel();
        setDebouncedValue("");
        onClear();
      }
    : undefined;

  const handleChange = (value: string) => {
    setSearchValue(value);
  };

  const startAccessoryJSX = startAccessory ?? (
    <div className="w-8 h-8 flex items-center justify-center">
      <SearchIcon size={18} />
    </div>
  );

  return (
    <Input
      {...rest}
      className={cn("min-w-0 w-full", className)}
      startAccessory={startAccessoryJSX}
      placeholder={placeholder ?? t("components.queryToolbar.search")}
      value={searchValue}
      onChange={handleChange}
      onClear={handleClear}
    />
  );
};
